/**
 * DesktopLayout Component
 * Shared layout for all desktop pages
 * GlobalHeader manages MarqueeBanner internally
 * Content padding adjusts based on marquee visibility
 *
 * Design Reference: docs/global-header-redesign.md v2.0
 */

import type { ReactNode } from 'react';
import GlobalHeader from '../common/GlobalHeader';
import { useMarquees } from '../../hooks/useMarquees';
import './DesktopLayout.css';

interface DesktopLayoutProps {
  children: ReactNode;
  /**
   * Center content vertically (for auth pages, etc.)
   * Design Reference: docs/auth-pages-design.md
   */
  centered?: boolean;
}

/**
 * DesktopLayout - Standard layout wrapper for all desktop pages
 *
 * Layout structure:
 * ┌─────────────────────────────────┐
 * │  MarqueeBanner (if enabled)      │ ← Fixed at top (managed by GlobalHeader)
 * ├─────────────────────────────────┤
 * │  GlobalHeader                    │ ← Fixed below marquee or at top
 * ├─────────────────────────────────┤
 * │  Page Content                    │ ← Main content area (can be centered)
 * └─────────────────────────────────┘
 *
 * Note: GlobalHeader internally manages MarqueeBanner rendering.
 * DesktopLayout checks marquee visibility to adjust content padding.
 * This mirrors the mobile pattern where pages use useMarquees to handle layout.
 *
 * @example
 * // Standard layout
 * <DesktopLayout>...</DesktopLayout>
 *
 * @example
 * // Centered layout (for auth pages, etc.)
 * <DesktopLayout centered>...</DesktopLayout>
 */
const DesktopLayout = ({ children, centered = false }: DesktopLayoutProps) => {
  const { enabled: marqueeEnabled, loading: marqueeLoading } = useMarquees();

  // Marquee is shown when both enabled and not loading (same logic as GlobalHeader)
  const shouldShowMarquee = marqueeEnabled && !marqueeLoading;

  return (
    <div className="desktop-layout">
      <GlobalHeader />
      <main className={`desktop-layout__content ${shouldShowMarquee ? 'desktop-layout__content--with-marquee' : ''} ${centered ? 'desktop-layout__content--centered' : ''}`}>
        {children}
      </main>
      <div className="desktop-layout__footer-bottom">
        <p>&copy; 2025 瑋瑋車業. All rights reserved.</p>
      </div>
    </div>
  );
};

export default DesktopLayout;
